/**
 * PreDeterminationProcessSection - Output section for pre-determination process
 *
 * Displays pre-determination process information as a top-level accordion section.
 */

import { AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import React from 'react';

import { FieldPathProvider } from '../context';
import { Field, SectionTriggerScore } from './Field';

interface PreDeterminationProcessSectionProps {
  data?: Record<string, unknown>;
  sectionId: string;
  entityType?: 'drug' | 'procedure';
}

export const PreDeterminationProcessSection: React.FC<PreDeterminationProcessSectionProps> = ({
  data,
  sectionId,
  entityType = 'drug',
}) => {
  const label = entityType === 'drug' ? 'Drug' : 'Procedure';

  return (
    <AccordionItem value={sectionId} className="border-b">
      <AccordionTrigger className="px-4 py-3 text-sm font-medium hover:bg-muted/50">
        <div className="flex items-center gap-2 flex-1">
          <span>{label} Pre Determination Process</span>
          <SectionTriggerScore subsectionKey="pre_determination_process" />
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 pb-4">
        <FieldPathProvider prefix={`${entityType}.pre_determination_process`}>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <Field
              label="Department Name"
              value={data?.pre_determination_department_name}
              fieldId="pre_determination_department_name"
            />
            <Field
              label="Turnaround Time"
              value={data?.pre_determination_turnaround_time}
              fieldId="pre_determination_turnaround_time"
            />
            <Field
              label="Fax Number"
              value={data?.pre_determination_fax_number}
              fieldId="pre_determination_fax_number"
            />
            <Field
              label="Phone Number"
              value={data?.pre_determination_phone_number}
              fieldId="pre_determination_phone_number"
            />
            <Field
              label="Website"
              value={data?.pre_determination_website}
              fieldId="pre_determination_website"
            />
            <Field
              label="Submission Methods"
              value={data?.pre_determination_submission_methods}
              fieldId="pre_determination_submission_methods"
            />
            <Field
              label="Notification Methods"
              value={data?.pre_determination_notification_methods}
              fieldId="pre_determination_notification_methods"
            />
            <Field
              label="Requirements"
              value={data?.pre_determination_requirements}
              fieldId="pre_determination_requirements"
            />
          </div>
        </FieldPathProvider>
      </AccordionContent>
    </AccordionItem>
  );
};
